import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "kroma_cart_v1";
const MAX_ITEMS = 20;
const MAX_QTY = 10;

const CartContext = createContext(null);

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* quota — o carrinho continua a funcionar em memória */
  }
}

const newKey = () => `it_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Carrinho de compras (persistido em localStorage).
 *
 * Cada artigo:
 *  { key, type: "product"|"custom", product_id?, name, image?, size?, shape?,
 *    finish?, unit_price (indicativo), quantity, settings?, mode?, photo? }
 *
 * O PREÇO FINAL é sempre calculado no servidor (POST /api/cart/quote e
 * POST /api/orders/cart); os valores locais servem apenas para mostrar.
 */
export function CartProvider({ children }) {
  const [items, setItems] = useState(load);

  useEffect(() => {
    save(items);
  }, [items]);

  // sincroniza entre abas
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setItems(load());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addItem = useCallback((item, qty = 1) => {
    let result = { ok: true };
    setItems((prev) => {
      // produtos iguais (mesmo id + mesmo acabamento) juntam-se numa linha
      const sig = JSON.stringify({
        t: item.type || "product",
        p: item.product_id || null,
        s: item.size || null,
        f: item.finish || null,
      });
      const idx = prev.findIndex(
        (i) =>
          JSON.stringify({
            t: i.type || "product",
            p: i.product_id || null,
            s: i.size || null,
            f: i.finish || null,
          }) === sig && (item.type || "product") === "product"
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          quantity: Math.min(MAX_QTY, (next[idx].quantity || 1) + qty),
        };
        return next;
      }
      if (prev.length >= MAX_ITEMS) {
        result = { ok: false, error: `Máximo de ${MAX_ITEMS} artigos no carrinho` };
        return prev;
      }
      return [...prev, { ...item, key: newKey(), quantity: Math.min(MAX_QTY, Math.max(1, qty)) }];
    });
    return result;
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const setQuantity = useCallback((key, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, quantity: Math.min(MAX_QTY, Math.max(0, qty)) } : i))
        .filter((i) => (i.quantity || 0) > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((n, i) => n + (i.quantity || 1), 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((n, i) => n + Number(i.unit_price || 0) * (i.quantity || 1), 0),
    [items]
  );

  // payload para o backend (sem campos locais)
  const payload = useMemo(
    () =>
      items.map((i) => ({
        type: i.type || "product",
        product_id: i.product_id || null,
        size: i.size || null,
        shape: i.shape || null,
        preview: i.type === "custom" ? i.preview || null : null,
        photo: i.type === "custom" ? i.photo || null : null,
        settings: i.type === "custom" ? i.settings || null : null,
        mode: i.type === "custom" ? i.mode || null : null,
        finish: i.finish || null,
        quantity: i.quantity || 1,
        // desenho do editor (animação dos fios / partilha)
        sequence: i.type === "custom" ? i.sequence || null : null,
        nail_positions: i.type === "custom" ? i.nail_positions || null : null,
        layers: i.type === "custom" ? i.layers || null : null,
      })),
    [items]
  );

  const value = useMemo(
    () => ({ items, addItem, removeItem, setQuantity, clear, count, subtotal, payload, MAX_QTY }),
    [items, addItem, removeItem, setQuantity, clear, count, subtotal, payload]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart deve ser usado dentro de <CartProvider>");
  return ctx;
}
